import { auth as authContracts } from '@personal-blog/contracts';
import type { FormValues } from '@viquard/doc-ui/forms';
import { useCallback } from 'react';
import { z } from 'zod';
import { useResetPasswordWithPhone } from '@/features/auth/api';
import { zodValidate } from '@/lib/zod-validate';
import { useAuthToast } from './use-auth-toast.js';

const FieldSchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/u, 'Verification code must be 6 digits'),
  password: authContracts.Password,
});

export interface ResetPasswordWithPhoneController {
  defaults: Record<string, string>;
  validate: ReturnType<typeof zodValidate>;
  submit: (values: FormValues) => Promise<void>;
  isPending: boolean;
  isSuccess: boolean;
  phone: string;
}

export function useResetPasswordWithPhoneController(opts: {
  phone: string;
  onSuccess?: () => void;
}): ResetPasswordWithPhoneController {
  const reset = useResetPasswordWithPhone();
  const { phone, onSuccess } = opts;
  const { notifyError } = useAuthToast();

  const submit = useCallback(
    async (values: FormValues) => {
      const code = typeof values.code === 'string' ? values.code : '';
      const password = typeof values.password === 'string' ? values.password : '';
      const confirmPassword =
        typeof values.confirmPassword === 'string' ? values.confirmPassword : '';
      try {
        const parsed = authContracts.ResetPasswordWithPhoneRequest.parse({
          phone,
          code,
          password,
          confirmPassword,
        });
        await reset.mutateAsync(parsed);
        onSuccess?.();
      } catch (err) {
        notifyError(err);
      }
    },
    [reset, phone, onSuccess, notifyError],
  );

  return {
    defaults: { code: '', password: '', confirmPassword: '' },
    validate: zodValidate(FieldSchema),
    submit,
    isPending: reset.isPending,
    isSuccess: reset.isSuccess,
    phone,
  };
}
